import {useMemo} from 'react';
import {Search} from 'lucide-react';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  InputGroupAddon
} from '@kroll-resolver-crisp/ui-components-react';
import {useLocations} from '@/api/hooks';
import {cn} from '@/lib/utils';

type Props = {
  /** ISO 3166-1 alpha-2 code, or '' for none. */
  readonly value: string;
  readonly onChange: (code: string) => void;
  readonly className?: string;
};

/**
 Single-select country combobox over the locations pack's countries (the rows carrying
 `attrs.countryCode`; regions have none). Carries the code, shows the name. The list is small
 enough (~250) to filter locally, unlike LanguageSelect's server search.
 */
export function CountrySelect({value, onChange, className}: Props) {
  const {data: locations} = useLocations();
  const countries = useMemo(
    () =>
      (locations ?? [])
        .map((l) => ({name: l.name, code: String(l.metadata?.countryCode ?? '')}))
        .filter((c) => c.code)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [locations]
  );
  const names = useMemo(() => countries.map((c) => c.name), [countries]);
  const selected = countries.find((c) => c.code === value)?.name ?? null;

  return (
    <Combobox
      items={names}
      value={selected}
      onValueChange={(name) => {
        onChange(countries.find((c) => c.name === name)?.code ?? '');
      }}>
      <ComboboxInput showClear placeholder="Any" className={cn('w-full', className)}>
        <InputGroupAddon>
          <Search />
        </InputGroupAddon>
      </ComboboxInput>
      <ComboboxContent>
        <ComboboxEmpty>No results</ComboboxEmpty>
        <ComboboxList>
          {(name: string) => (
            <ComboboxItem key={name} value={name}>
              {name}
            </ComboboxItem>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
}
